<script>
import { required } from "@vuelidate/validators";
import { notInt } from "@/utils/form-validators";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Form from "@/components/molecules/form";
import FormFieldset from "@/components/molecules/form-fieldset";
import StateOverlay from "@/components/molecules/state-overlay";
import Button from "@/components/atoms/button";
import Popin from "@/components/molecules/popin";
import Titre from "@/components/atoms/titre";
import Divider from "@/components/atoms/divider";
import Alert from "@/components/molecules/alert";
import {
  infosFacturationProFormEmits,
  infosFacturationProFormProps,
  useInfosFacturationProForm
} from "./mixin";
import { useVuelidate } from "@vuelidate/core";

export default {
  name: "InfosFacturationProFormMobile",
  components: {
    Button,
    FormInput,
    FormGroup,
    Form,
    FormFieldset,
    StateOverlay,
    Popin,
    Titre,
    Divider,
    Alert
  },
  props: {
    ...infosFacturationProFormProps()
  },
  validations() {
    return {
      model: {
        pro_ref: {
          required
        },
        pro_passee_par: {
          required,
          notInt
        }
      }
    };
  },
  emits: [...infosFacturationProFormEmits()],
  setup(props, context) {
    const confirmation = ref(null);

    const {
      model,
      askConfirm,
      save,
      hydrateModel,
      displayCgv,
      nbValidations,
      getState,
      getErrorMessage,
      isLoadingComputed
    } = useInfosFacturationProForm(props, context, confirmation);

    onMounted(hydrateModel);

    return {
      model,
      askConfirm,
      save,
      hydrateModel,
      displayCgv,
      confirmation,
      v$: useVuelidate(),
      nbValidations,
      getState,
      getErrorMessage,
      isLoadingComputed
    };
  }
};
</script>

<template>
  <Form
    :error="error"
    @submit.prevent="askConfirm(v$)"
  >
    <StateOverlay
      :is-loading="isLoadingComputed"
      overlay
    >
      <p class="u-font-16 u-mb-md">
        Veuillez indiquer les références internes de votre commande :
      </p>
      <FormFieldset>
        <FormGroup
          size="xlg"
          class="u-mb-md"
          :state="getState(v$,'pro_ref', 'model')"
        >
          <FormInput
            v-model="model.pro_ref"
            size="xlg"
            type="text"
            :state="getState(v$,'pro_ref', 'model')"
          >
            Référence interne de votre bon de commande *
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("pro_ref","Merci de renseigner une référence interne pour votre bon de commande") }}
            </p>
          </template>
        </FormGroup>
        <FormGroup
          size="xlg"
          class="u-mb-md"
          :state="getState(v$,'pro_passee_par', 'model')"
        >
          <FormInput
            v-model="model.pro_passee_par"
            size="xlg"
            type="text"
            :state="getState(v$,'pro_passee_par', 'model')"
          >
            Commande passée par M./Mme *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.pro_passee_par.notInt.$invalid || v$.model.pro_passee_par.required.$invalid">
                Veuillez entrer un nom valide : compris entre 1 et 40 caractères maximum et au format texte
              </template>
              <template v-else>
                {{ getErrorMessage("pro_passee_par","Merci de renseigner le nom de la personne qui passe la commande") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          size="xlg"
          class="u-mb-md"
          :state="getState(v$,'pro_beneficiaire', 'model')"
        >
          <FormInput
            v-model="model.pro_beneficiaire"
            size="xlg"
            type="text"
            :state="getState(v$,'pro_beneficiaire', 'model')"
          >
            Bénéficiaire
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("pro_beneficiaire","Merci de renseigner un nom de bénéficiaire valide") }}
            </p>
          </template>
        </FormGroup>
        <FormGroup
          size="xlg"
          class="u-mb-md"
          :state="getState(v$,'pro_adr_beneficiaire', 'model')"
        >
          <FormInput
            v-model="model.pro_adr_beneficiaire"
            size="xlg"
            type="textarea"
            :state="getState(v$,'pro_adr_beneficiaire', 'model')"
          >
            Adresse du Bénéficiaire
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("pro_adr_beneficiaire","Merci de renseigner une adresse de bénéficiaire valide") }}
            </p>
          </template>
        </FormGroup>
        <span>
          En validation je déclare avoir pris connaissance et accepter
          <Button
            link
            class="c-infos-facturation-pro-form__button-link"
            @click="displayCgv"
          >
            les conditions générales de vente
          </Button>
        </span>
        <div class="u-text-center u-mt-md">
          <Button
            class="c-checkout-pro__next-btn"
            variant="secondary"
            radius="full"
            size="md"
            type="submit"
            value="Valider ma commande"
          />
        </div>
      </FormFieldset>
      <Divider
        dotted
        class="u-mb-md u-mt-md"
      />
      <Titre
        tag="h3"
        size="sm"
      >
        Retrait de ma commande
      </Titre>
      <Alert
        variant="primary-lighter"
        radius="md"
        class="u-m-sm"
      >
        <p class="u-text-center">
          Cette commande sera mise à votre disposition au drive de votre magasin
          <strong>
            <span class="u-color-secondary">
              dans un délai de {{ checkout.attributes.modalitiesDelay }}
              à compter de la confirmation de votre commande.
            </span>
          </strong>
        </p>
      </Alert>
      <Alert
        v-if="infoNonRetrait"
        has-icon
        icon-name="info-circle"
        icon-color="primary-light"
        icon-size="sm"
        radius="md"
        class="u-m-sm"
      >
        {{ infoNonRetrait }}
      </Alert>
    </StateOverlay>
    <Popin
      ref="confirmation"
      aria-label="Valider les informations"
      slide-up
    >
      <Titre
        variant="primary"
        tag="h2"
        class="u-text-center u-ml-sm u-mr-sm u-mb-sm"
      >
        Je valide ces informations et lance la préparation de ma commande
      </Titre>
      <p class="u-mb-sm">
        Référence interne de votre bon de commande : <strong>{{ model.pro_ref }}</strong>
      </p>
      <p class="u-mb-sm">
        Commande passée par M/Mme : <strong>{{ model.pro_passee_par }}</strong>
      </p>
      <p
        v-if="model.pro_beneficiaire"
        class="u-mb-sm"
      >
        Bénéficiaire : <strong>{{ model.pro_beneficiaire }}</strong>
      </p>
      <p
        v-if="model.pro_adr_beneficiaire"
        class="u-mb-sm"
      >
        Adresse du bénéficiaire : <strong>{{ model.pro_adr_beneficiaire }}</strong>
      </p>
      <div class="u-flex u-flex-justify-around">
        <Button
          variant="primary"
          outline
          radius="full"
          size="md"
          @click="$refs.confirmation.hide()"
        >
          Changer
        </Button>
        <Button
          variant="secondary"
          radius="full"
          size="md"
          @click="save(v$)"
        >
          Valider
        </Button>
      </div>
    </Popin>
  </Form>
</template>

<style lang="scss" scoped>
.c-infos-facturation-pro-form__button-link {
  font-size: 1.6rem;
  display: initial;
}
</style>
